import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import {
  addCodesDomainForStaff, listCodesDomainsForStaff, listPausedEyemedLoginsForStaff,
  restartEyemedLoginForStaff, type CodesDomainListing, type PausedEyemedLogin,
} from '../services/staff'
import { isCodesDomainName } from '../../shared/codesDomain'

/** A pause cause by its state name; the raw key rides along as the tooltip. */
const CAUSE_LABEL: Record<string, string> = {
  no_email: 'No email',
  email_without_code: 'Email without code',
  code_rejected: 'Code rejected',
  run_ended: 'Run ended',
}

function formatTime(iso: string): string {
  const at = Date.parse(iso)
  return Number.isFinite(at) ? new Date(at).toLocaleString() : '—'
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

function formatDay(iso: string): string {
  const at = Date.parse(iso)
  return Number.isFinite(at) ? new Date(at).toLocaleDateString() : '—'
}

/**
 * Portal Codes — the staff section listing the codes domains staff bought for
 * EyeMed's login codes. Each practice is given a free one, for good, the first
 * time it needs an address; no two practices ever share one, so when none is
 * free a practice needing its first address gets none until staff add one.
 * Domains are added here after being bought and set up by the runbook
 * (docs/codes-domain-setup.md); the list is read through
 * `listCodesDomainsForStaff` and an add goes through `addCodesDomainForStaff`
 * (staff-only, audited).
 */
export default function PortalCodes() {
  const [listing, setListing] = useState<CodesDomainListing | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [adding, setAdding] = useState(false)
  const [paused, setPaused] = useState<PausedEyemedLogin[] | null>(null)
  const [pausedError, setPausedError] = useState<string | null>(null)
  const [restarting, setRestarting] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setListing(await listCodesDomainsForStaff())
    } catch (err) {
      Sentry.captureException(err)
      setListing(null)
      setError(`Couldn't load the codes domains. ${errorMessage(err, '')}`.trim())
    }
  }, [])

  const loadPaused = useCallback(async () => {
    setPausedError(null)
    try {
      setPaused(await listPausedEyemedLoginsForStaff())
    } catch (err) {
      Sentry.captureException(err)
      setPaused(null)
      setPausedError(`Couldn't load the paused logins. ${errorMessage(err, '')}`.trim())
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => { void loadPaused() }, [loadPaused])

  async function restart(login: PausedEyemedLogin) {
    setRestarting(login.loginId)
    try {
      await restartEyemedLoginForStaff(login.loginId, login.pausedAt)
      toast.success(`${login.username} restarted`)
    } catch (err) {
      Sentry.captureException(err)
      toast.error(errorMessage(err, 'Could not restart the login.'))
    } finally {
      setRestarting(null)
      // Either way the list shows the login as it now is.
      await loadPaused()
    }
  }

  async function add() {
    const domain = draft.trim().toLowerCase()
    if (!isCodesDomainName(domain)) {
      toast.error('Enter a domain name, like quietfern.com.')
      return
    }
    setAdding(true)
    try {
      await addCodesDomainForStaff(domain)
      toast.success(`${domain} added`)
      setDraft('')
      await load()
    } catch (err) {
      Sentry.captureException(err)
      toast.error(errorMessage(err, 'Could not add the domain.'))
    } finally {
      setAdding(false)
    }
  }

  return (
    <div className="staff-emulator">
      <header className="staff-emulator__head">
        <div>
          <h1>Portal Codes</h1>
          <p className="staff-console-subtitle">
            The domains EyeMed&rsquo;s login codes arrive at. Each practice is given one of its own
            the first time it needs an address.
          </p>
        </div>
      </header>
      <div className="staff-console-toolbar">
        <input
          type="text"
          placeholder="Add a domain you bought, e.g. quietfern.com"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim() && !adding) void add() }}
          disabled={adding}
          aria-label="Domain to add"
        />
        <button className="staff-view-button" onClick={() => { void add() }} disabled={adding || !draft.trim()}>
          {adding ? 'Adding…' : 'Add domain'}
        </button>
      </div>
      {error && <p className="auth-error">{error}</p>}
      {!listing && !error && <p className="staff-console-loading">Loading codes domains…</p>}
      {listing && (
        <>
          <p className="staff-console-subtitle" data-testid="codes-free">
            {listing.free} free of {listing.domains.length}
          </p>
          {listing.free === 0 ? (
            <p className="auth-error" role="alert" data-testid="codes-none-free">
              No domain is free: a practice that needs its first EyeMed address gets none until a domain is added.
            </p>
          ) : listing.low && (
            <p className="auth-error" role="alert" data-testid="codes-low">
              Few free domains left — buy more and add them here.
            </p>
          )}
          <ul className="staff-org-list">
            {listing.domains.map((d) => (
              <li key={d.domain} className="staff-org-row">
                <div className="staff-org-meta">
                  <span className="staff-org-name">{d.domain}</span>
                  <span className="staff-org-id">Added by {d.addedBy || 'unknown'} on {formatDay(d.addedAt)}</span>
                </div>
                <div className="staff-org-actions">
                  <span className="sms-mgmt-routes__label">{d.orgId ? `Held by ${d.orgName ?? d.orgId}` : 'Free'}</span>
                </div>
              </li>
            ))}
            {listing.domains.length === 0 && <li className="staff-org-empty">No codes domains yet.</li>}
          </ul>
        </>
      )}

      <header className="staff-emulator__head">
        <div>
          <h2>Paused EyeMed logins</h2>
          <p className="staff-console-subtitle">
            Logins paused by a fault on our side. Fix the cause, then restart the login so the robot tries it again.
          </p>
        </div>
      </header>
      {pausedError && <p className="auth-error">{pausedError}</p>}
      {!paused && !pausedError && <p className="staff-console-loading">Loading paused logins…</p>}
      {paused && (
        <ul className="staff-org-list" data-testid="paused-logins">
          {paused.map((login) => (
            <li key={login.loginId} className="staff-org-row">
              <div className="staff-org-meta">
                <span className="staff-org-name">{login.username}</span>
                <span className="staff-org-id">
                  {login.clinics.map((c) => `${c.orgName} · ${c.clinicName}`).join(', ') || 'No clinic uses it'}
                </span>
                <span className="staff-org-id">Paused {formatTime(login.pausedAt)}</span>
              </div>
              <div className="staff-org-actions">
                <span className="sms-mgmt-routes__label" title={login.cause}>
                  {CAUSE_LABEL[login.cause] ?? login.cause}
                </span>
                <button
                  className="staff-view-button"
                  onClick={() => { void restart(login) }}
                  disabled={restarting === login.loginId}
                >
                  {restarting === login.loginId ? 'Restarting…' : 'Restart'}
                </button>
              </div>
            </li>
          ))}
          {paused.length === 0 && <li className="staff-org-empty">No login is paused for us to fix.</li>}
        </ul>
      )}
    </div>
  )
}
